import qs from "qs";
import { getStrapiURL } from "./api-helpers"
import { withServerUserAgent } from "./server-user-agent";
import { rewriteAssetUrlsDeep } from "./asset-cdn";
import {
  ARTICLE_CARD,
  ARTICLES_BY_CATEGORY_QUERY,
  MOBILE_APP_POPULATE,
  PAGE_POPULATE,
  RELATED_ARTICLE_POPULATE,
  SOLUTION_POPULATE,
} from "./populate";

type Config = {
  params?: Record<string, any>
  fetchOptions?: RequestInit
}

export const createApiInstance = () => {
  const token = process.env.NEXT_PUBLIC_STRAPI_API_TOKEN;

  const getUrl = (path: string, config?: Config) => {
    return getStrapiURL( '/api' + path + (config?.params ? `?${ qs.stringify(config.params)}` : '') );
  }

  const baseOptions = {
    next: { revalidate: 60, tags: ["strapi"] },
    headers: withServerUserAgent({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    })
  }

  return {
    get: async (path: string, config?: Config) => {
      const url = getUrl(path, config);
      const response = await fetch(url, {
        method: "GET",
        ...baseOptions,
        ...config?.fetchOptions
      });
      // Map S3 URLs to CloudFront before the payload reaches the app, so no
      // presigned URL is left to be serialized into the RSC stream.
      return rewriteAssetUrlsDeep(await response.json());
    },
    post: async (path: string, data: any, config?: Config & { headers?: Record<string, string> }) => {
      const url = getUrl(path, config);
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(data),
        ...baseOptions,
        ...config?.fetchOptions,
        headers: {
          ...baseOptions.headers,
          ...(config?.headers || {}),
        },
      });

      const text = await response.text();

      try {
        return text ? rewriteAssetUrlsDeep(JSON.parse(text)) : null;
      } catch {
        return null;
      }
    }
  }
}

const ApiInstance = createApiInstance()

export const post = async (action: string, data: any, config?: any) => {
  return await ApiInstance.post(action, data, config);
}
export const get = async (action: string, data: any) => {
  return await ApiInstance.get(action, data);
}

export const getPreviewArticle = async (params: Record<string, any>) => {
  return await ApiInstance.get(`/articles/preview/${params}`)
}

export const getArticles = async (params: Record<string, any>) => {
  return await ApiInstance.get('/articles', {params});
}

export const getRedirects = async () => {
  return await ApiInstance.get('/redirects');
};

export const getArticlesByCategory = async (categorySlug: string) => {
  return await ApiInstance.get('/articles', {
    params: {
      sort: {createdAt: "desc"},
      filters: {
        category: {
          slug: categorySlug,
        },
      },
      ...ARTICLES_BY_CATEGORY_QUERY,
    }
  });
}

export const getPageBySlug = async (pageSlug: string | string[], lang: string, preview: boolean = false) => {
  const filterParams: { slug: string | string[], page_category?: { path: string } } = {
    slug: Array.isArray(pageSlug) ? pageSlug.slice(-1) : pageSlug
  };

  if (Array.isArray(pageSlug) && pageSlug.length > 1) {
    filterParams.page_category = { path: pageSlug.slice(0, -1).join('/') };
  }

  return await ApiInstance.get('/pages', {
    params: {
      filters: filterParams,
      locale: lang,
      populate: PAGE_POPULATE,
      ...(preview && {
        publicationState: "preview",
      }),
    }
  });
}

export type ChargingLocationFilters = {
  availability?: boolean;
  standard?: boolean;
  rapid?: boolean;
  type2?: boolean;
  ccsCombo2?: boolean;
  chademo?: boolean;
  accessibleFriendly?: boolean;
  nfcOnly?: boolean;
};

type ClusteredChargingLocationsRequest = {
  filters?: ChargingLocationFilters;
  center?: { lat: number; lng: number };
  bounds?: [number, number, number, number];
  zoomLevel?: number;
  cpos?: string;
};

const getIsFastOnlyParam = (filters?: ChargingLocationFilters) => {
  return filters?.standard ? "1" : "0";
};

const getKwParam = (filters?: ChargingLocationFilters) => {
  return filters?.rapid ? "150" : "0";
};

const buildClusteredChargingLocationsQuery = (request?: ClusteredChargingLocationsRequest) => {
  const params = new URLSearchParams();
  const [long1, lat1, long3, lat3] = request?.bounds ?? [];
  const isFastOnly = getIsFastOnlyParam(request?.filters);
  const kW = getKwParam(request?.filters);

  params.set("available", request?.filters?.availability ? "1" : "0");
  params.set("kW", kW);
  params.set("isFastOnly", isFastOnly);
  params.set("accessibleFriendly", request?.filters?.accessibleFriendly ? "1" : "0");
  params.set("typeTwo", request?.filters?.type2 ? "1" : "0");
  params.set("ccsTypeTwo", request?.filters?.ccsCombo2 ? "1" : "0");
  params.set("chademo", request?.filters?.chademo ? "1" : "0");
  params.set("nfcOnly", request?.filters?.nfcOnly ? "1" : "0");
  params.set("cpos", request?.cpos ?? "172");

  if (request?.center) {
    params.set("lat", String(request.center.lat));
    params.set("long", String(request.center.lng));
  }

  if (typeof lat1 === "number" && typeof long1 === "number") {
    params.set("lat1", String(lat1));
    params.set("long1", String(long1));
  }

  if (typeof lat3 === "number" && typeof long3 === "number") {
    params.set("lat3", String(lat3));
    params.set("long3", String(long3));
  }

  if (typeof request?.zoomLevel === "number") {
    params.set("zoomLevel", String(Math.max(0, Math.min(20, Math.round(request.zoomLevel)))));
  }

  return params.toString();
};

export const getChargingLocations = async (
  name: string = "",
  address: string = name
) => {
  return await ApiInstance.get("/charging-locations", {
    params: {
      filters: {
        $or: {
          0: {
            name: {
              $containsi: name,
            },
          },
          1: {
            address: {
              $containsi: address,
            },
          },
        },
      },
      pagination: {
        limit: 500,
      },
    },
  });
};

export const getClusteredChargingLocations = async (
  request?: ClusteredChargingLocationsRequest
) => {
  const query = buildClusteredChargingLocationsQuery(request);
  return await ApiInstance.get(`/partners/charge-points/clustered?${query}`, {
    fetchOptions: {
      cache: "no-store",
    },
  });
};

export const getChargingLocationById = async (id: string | number) => {
  return await ApiInstance.get(`/partners/charge-points/${id}`, {
    fetchOptions: {
      cache: "no-store",
    },
  });
}

export const getSearchData = async () => {
  return await ApiInstance.get('/search?locale=ro');
}

export const getGlobal = async (lang: string) => {
  return await ApiInstance.get('/global', {
    params: {
      populate: [
        "metadata.shareImage",
        "favicon",
        "notificationBanner.link",
        "navbar.links",
        "navbar.navigationButton",
        "navbar.navigationButton.link",
        "navbar.navigationButton.link.page",
        "navbar.navbarLogo.logoImg",
        "navbar.navigationButton.submenuColumns",
        "navbar.navigationButton.submenuColumns.link",
        "navbar.navigationButton.submenuColumns.link.page",
        "navbar.navigationButton.submenuColumns.subItems",
        "navbar.navigationButton.submenuColumns.subItems.page",
        "navbar.navigationButton.submenuColumns.subItems.page.page_category",
        "footer",
        "footer.footerLogo.logoImg",
        "footer.footerSections.links",
        "footer.footerSections.links.page",
        "footer.footerSections.links.page.page_category",
        "footer.socialLinks",
        "maintenance",
        "maintenance.backgroundImage",
      ],
      locale: lang,
    }
  });
}

export const fetchArticles = async (limit: number) => {
  return await ApiInstance.get('/articles', {
    params: {
      locale: "ro",
      sort: { createdAt: "desc" },
      pagination: { page: 1, pageSize: limit },
      ...ARTICLE_CARD,
    }
  });
}

// The singletons behind ShowHero / ShowSolutions / ShowArticles, each one with
// the populate its component needs.
const SINGLE_TYPE_POPULATE: Record<string, Record<string, any>> = {
  "mobile-app": MOBILE_APP_POPULATE,
  solution: SOLUTION_POPULATE,
  "related-article": RELATED_ARTICLE_POPULATE,
};

export const fetchArticleData = async (element: string, deep = false) => {
  const populate = deep ? SINGLE_TYPE_POPULATE[element] : undefined;

  return await ApiInstance.get(`/${element}`, {
    params: {
      locale: "ro",
      ...(populate && { populate }),
    }
  });
}
